import { useEffect, useRef } from 'react';
import { performanceService } from '@/services/PerformanceService';

/**
 * Simple hook for measuring component performance
 * Ensures consistent hook execution and avoids syntax issues
 */
export function usePerformanceMetrics(componentName: string) {
  // Track metrics across renders
  const metrics = useRef({
    mountTime: performance.now(),
    renderCount: 0,
    totalRenderTime: 0,
    domNodesCount: 0,
    isFirstRender: true
  });
  
  // Record basic component mount metrics
  useEffect(() => {
    // Track first render and mount time
    if (metrics.current.isFirstRender) {
      const mountDuration = performance.now() - metrics.current.mountTime;
      performanceService.recordMetric(componentName, 'mountTime', mountDuration);
      metrics.current.isFirstRender = false;
    }
    
    // Count renders
    metrics.current.renderCount += 1;
    performanceService.recordMetric(componentName, 'renderCount', metrics.current.renderCount);
    
    // Measure DOM size
    const domNodes = document.querySelectorAll('*').length;
    metrics.current.domNodesCount = domNodes;
    performanceService.recordMetric(componentName, 'domNodes', domNodes);
    
    // Cleanup
    return () => {
      // Nothing to clean up
    };
  }, [componentName]);
  
  // Simple method to measure operation time
  const measureOperation = (operationName: string) => {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      performanceService.recordMetric(componentName, operationName, duration);
      return duration;
    };
  };
  
  // Measure render time
  const measureRender = () => {
    const startTime = performance.now();
    
    return () => {
      const duration = performance.now() - startTime;
      performanceService.recordMetric(componentName, 'renderTime', duration);
      metrics.current.totalRenderTime += duration;
      return duration;
    };
  };
  
  // Get current metrics
  const getCurrentMetrics = () => {
    return {
      renderCount: metrics.current.renderCount,
      totalRenderTime: metrics.current.totalRenderTime,
      domNodesCount: metrics.current.domNodesCount
    };
  };
  
  // Mark interactive time
  const markInteractive = () => {
    const timeToInteractive = performance.now() - metrics.current.mountTime;
    performanceService.recordMetric(componentName, 'timeToInteractive', timeToInteractive);
  };
  
  return {
    measureOperation,
    measureRender,
    getCurrentMetrics,
    markInteractive
  };
}
